import type {
  PlaceNode,
  ScheduleListInRangeItem,
  ScheduleListInRangeItemInfo,
} from '@ulysses-inc/harami_api_client'
// https://github.com/import-js/eslint-plugin-import/issues/1479
import { colors, white } from '@kmns-internal/furikake'
import { CircleOutlined, Close } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { parse } from 'date-fns' // eslint-disable-line import/no-duplicates
// eslint-disable-next-line import/no-duplicates
import ja from 'date-fns/locale/ja'
import { parse as parseQueryString } from 'qs'
import type React from 'react'
import { type JSX, useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { type RouteComponentProps, useHistory } from 'react-router-dom'
import { Header } from 'src/components/header/Header'
import { HeaderBackButtonMui } from 'src/components/header/HeaderBackButtonMui'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import {
  ScheduleStatus,
  getScheduleStatus,
} from 'src/exShared/util/scheduleListInRange/convertScheduleListInRange'
import PlaceCalendar from 'src/features/dashboardPlace/PlaceCalendar'
import type { RootState } from 'src/state/store'
import placesOperations from '../../state/ducks/places/operations'
import scheduleListInRangeItemsOperations from '../../state/ducks/scheduleListInRangeItems/operations'

interface StateProps {
  placeNodeUUID: string
  targetDate: Date

  places: PlaceNode[]
  scheduleListInRangeItems: ScheduleListInRangeItem[]
  isLoading: boolean
}

interface DispatchProps {
  getPlaces: () => void
  getPlaceScheduleListInRangeItems: (targetDate: Date) => void
}

interface NavigationProps {
  goBack: () => void
  goReportResult: (reportUUID: string, templateId: number) => void
}

type PlaceDashboardProps = StateProps & DispatchProps & NavigationProps

const useStateProps = (placeNodeUUID: string): StateProps => {
  const history = useHistory()
  const qs = parseQueryString(history.location.search, {
    ignoreQueryPrefix: true,
  })
  return {
    placeNodeUUID: placeNodeUUID,
    targetDate: useSelector((state: RootState) => {
      if (
        state.scheduleListInRangeItemsState.placeScheduleListInRangeItems
          .targetDate
      ) {
        return state.scheduleListInRangeItemsState.placeScheduleListInRangeItems
          .targetDate
      }
      return parse(
        qs['targetDate'] as string,
        'yyyy-MM-dd',
        new Date(2000, 0, 1),
        { locale: ja },
      )
    }, shallowEqual),
    places: useSelector(
      (state: RootState) => state.placesState.places.places,
      shallowEqual,
    ),
    scheduleListInRangeItems: useSelector(
      (state: RootState) =>
        state.scheduleListInRangeItemsState.placeScheduleListInRangeItems
          .scheduleListInRangeItems,
      shallowEqual,
    ),
    isLoading: useSelector(
      (state: RootState) =>
        state.scheduleListInRangeItemsState.placeScheduleListInRangeItems
          .isLoading,
      shallowEqual,
    ),
  }
}

const useReducerProps = (stateProps: StateProps): DispatchProps => {
  const dispatch = useDispatch()

  return {
    getPlaces: () => {
      placesOperations.getPlaces(dispatch, {})
    },
    getPlaceScheduleListInRangeItems: (targetDate: Date) => {
      scheduleListInRangeItemsOperations.getPlaceScheduleListInRangeItems(
        dispatch,
        {
          placeNodeId: { $in: [stateProps.placeNodeUUID] },
          startDate: targetDate,
          endDate: targetDate,
          isNeedInvalidCount: 1,
        },
        'Asia/Tokyo',
      )
    },
  }
}

const useNavigationProps = (): NavigationProps => {
  const history = useHistory()

  return {
    goBack: () => {
      history.goBack()
    },
    goReportResult: (reportUUID: string, templateId: number) => {
      history.push(`/reports/${reportUUID}?templateId=${templateId}`)
    },
  }
}

const PlaceDashboardContainer: React.FC<
  RouteComponentProps<{ placeNodeUUID: string }>
> = (_props: RouteComponentProps<{ placeNodeUUID: string }>) => {
  const stateProps = useStateProps(_props.match.params['placeNodeUUID'])
  const dispatchProps = useReducerProps(stateProps)
  const navigationProps = useNavigationProps()
  const props = { ...stateProps, ...dispatchProps, ...navigationProps }
  return <PlaceDashboardScene {...props} />
}

type DataSource = {
  key: string
  scheduleName: string
  invalidCount: number
  status: ScheduleStatus
  templateId?: number
  reportUUID?: string
}

type TableColumn = {
  title: () => JSX.Element
  dataIndex: string
  align: 'center' | 'left'
  width?: number
  render: (value: any, row: any) => any
}

const PlaceDashboardScene: React.FC<PlaceDashboardProps> = (
  props: PlaceDashboardProps,
) => {
  const [dataSource, setDataSource] = useState<DataSource[]>([])
  const [targetDate, setTargetDate] = useState<Date>(props.targetDate)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setTargetDate(props.targetDate)
    props.getPlaces()
    props.getPlaceScheduleListInRangeItems(props.targetDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const dataSource: DataSource[] = []
    props.scheduleListInRangeItems.forEach((item, rangeIndex) => {
      item.infos?.forEach(
        (info: ScheduleListInRangeItemInfo, infoIndex: number) => {
          dataSource.push({
            key: `${rangeIndex}-${infoIndex}`,
            scheduleName: info.scheduleName || '',
            invalidCount:
              info.report?.invalidCount !== undefined
                ? info.report?.invalidCount
                : 0,
            status: getScheduleStatus(info),
            templateId: info.templateId,
            reportUUID: info.report?.uuid,
          })
        },
      )
    })

    // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
    setDataSource(dataSource)
  }, [props.scheduleListInRangeItems])

  const renderStatusIcon = (status: ScheduleStatus, disabled: boolean) => {
    switch (status) {
      case ScheduleStatus.COMPLETED: {
        return (
          <Box
            sx={{
              background: colors.success.selected,
              color: colors.success.main,
              width: 56,
              height: 56,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${colors.success.main}`,
              '&:hover': {
                background: colors.success.hover,
                border: `1px solid ${colors.success.main}`,
              },
              borderRadius: 1,
            }}
          >
            <CircleOutlined
              style={{
                color: colors.success.main,
                fill: colors.success.main,
                stroke: colors.success.main,
                strokeWidth: 2,
              }}
            />
          </Box>
        )
      }
      case ScheduleStatus.INCOMPLETE: {
        if (disabled) {
          return (
            <Box
              sx={{
                background: colors.error.hover,
                color: colors.error.main,
                width: 56,
                height: 56,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
              }}
            >
              <Close
                style={{
                  color: colors.error.main,
                  fill: colors.error.main,
                  stroke: colors.error.main,
                  strokeWidth: 2,
                }}
              />
            </Box>
          )
        }
        return (
          <Box
            sx={{
              background: colors.error.selected,
              color: colors.error.main,
              width: 56,
              height: 56,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${colors.error.main}`,
              borderRadius: 1,
              '&:hover': {
                background: colors.error.hover,
                border: `1px solid ${colors.error.main}`,
              },
            }}
          >
            <Close
              style={{
                color: colors.error.main,
                fill: colors.error.main,
                stroke: colors.error.main,
                strokeWidth: 2,
              }}
            />
          </Box>
        )
      }
      default: {
        return (
          <Box
            sx={{
              width: 56,
              height: 56,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.text.disabled,
            }}
          >
            -
          </Box>
        )
      }
    }
  }

  const columns: TableColumn[] = [
    {
      title: () => <>スケジュール名</>,
      dataIndex: 'scheduleName',
      align: 'left',
      render: (value: any, _: any) => {
        return <div>{value}</div>
      },
    },
    {
      title: () => <>ルール逸脱</>,
      dataIndex: 'invalidCount',
      align: 'center',
      width: 140,
      render: (value: any, row: any) => {
        if (!row.reportUUID || !row.templateId) {
          return (
            <Box
              sx={{
                width: 56,
                height: 56,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.text.disabled,
              }}
            >
              -
            </Box>
          )
        }
        if (value === 0) {
          return (
            <Box
              sx={{
                width: 56,
                height: 56,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: colors.text.primary,
              }}
            >
              0
            </Box>
          )
        }
        return (
          <Box
            sx={{
              width: 56,
              height: 56,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: colors.error.hover,
              borderRadius: 1,
              color: colors.error.main,
            }}
          >
            {value}
          </Box>
        )
      },
    },
    {
      title: () => <>実施状況</>,
      dataIndex: 'status',
      align: 'center',
      width: 140,
      render: (value: any, row: any) => {
        const status = value as ScheduleStatus
        const disabled = !row.reportUUID || !row.templateId
        return (
          <Box
            onClick={() => {
              if (disabled) {
                return
              }
              props.goReportResult(row.reportUUID, row.templateId)
            }}
            style={!disabled ? { cursor: 'pointer' } : {}}
            tabIndex={0}
            onKeyPress={e => {
              if (e.key === 'Enter' && !disabled) {
                props.goReportResult(row.reportUUID, row.templateId)
              }
            }}
          >
            {renderStatusIcon(status, disabled)}
          </Box>
        )
      },
    },
  ]
  return (
    <>
      <Header>
        <HeaderBackButtonMui onClick={() => props.goBack()} />
        <Stack direction="row" alignItems="center" spacing={2}>
          <HeaderTitle
            title={
              props.places.find(place => place.uuid === props.placeNodeUUID)
                ?.place?.name || ''
            }
          />
          <PlaceCalendar
            targetDate={targetDate}
            placeNodeUUID={props.placeNodeUUID}
            changeTargetDate={(newTargetDate: Date) => {
              setTargetDate(newTargetDate)
              props.getPlaceScheduleListInRangeItems(newTargetDate)
            }}
          />
        </Stack>
      </Header>
      <Box position="relative" width="100%">
        {props.isLoading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1,
            }}
          >
            <CircularProgress data-testid="dashboard-loading" />
          </Box>
        )}
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ backgroundColor: white }}>
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell
                    key={col.dataIndex}
                    align={col.align}
                    sx={{ width: col.width }}
                    size="small"
                  >
                    {col.title()}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dataSource.map(row => (
                <TableRow key={row.key}>
                  {columns.map(col => (
                    <TableCell
                      key={col.dataIndex}
                      align={col.align}
                      sx={{
                        width: col.width,
                      }}
                      size="small"
                    >
                      {col.render(row[col.dataIndex as keyof DataSource], row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  )
}

export default PlaceDashboardContainer
